// App.js
import { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';

// Generer tidspunkt-array én gang (utenfor komponenten)
const HOURS = (() => {
  const result = [];
  for (let i = 0; i < 24; i++) {
    // Dagtid (6-22): hvert 20. minutt, natt: hver time
    if (i >= 6 && i < 22) {
      result.push(`${i}:00`);
      result.push(`${i}:20`);
      result.push(`${i}:40`);
    } else {
      result.push(`${i}:00`);
    }
  }
  return result;
})();

function App() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [image, setImage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageCache, setImageCache] = useState({});
  const [apiBaseUrl, setApiBaseUrl] = useState(null);
  const [isLatestMode, setIsLatestMode] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isHdMode, setIsHdMode] = useState(false);
  const [hdImage, setHdImage] = useState(null);
  const [hdLoading, setHdLoading] = useState(false);
  const [yearThumbnails, setYearThumbnails] = useState({
    minusTwo: null, minusOne: null, plusOne: null, plusTwo: null
  });

  // Ref for tracking blob URLs to revoke on unmount
  const blobUrlsRef = useRef(new Set());

  // Determine API URL based on network
  useEffect(() => {
    const determineApiUrl = () => {
      const externalApiUrl = process.env.REACT_APP_API_URL_EXTERNAL || '__API_URL_EXTERNAL_PLACEHOLDER__';
      const internalApiUrl = process.env.REACT_APP_API_URL_INTERNAL || '__API_URL_INTERNAL_PLACEHOLDER__';

      const isLocalNetwork = () => {
        try {
          const hostName = window.location.hostname;
          return hostName === 'localhost' ||
                 hostName === '127.0.0.1' ||
                 hostName.startsWith('192.168.') ||
                 hostName.startsWith('10.') ||
                 hostName.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./);
        } catch (e) {
          console.error('Error determining network type:', e);
          return false;
        }
      };

      if (isLocalNetwork()) {
        console.log('Using internal network API URL:', internalApiUrl);
        setApiBaseUrl(internalApiUrl);
      } else {
        console.log('Using external API URL:', externalApiUrl);
        setApiBaseUrl(externalApiUrl);
      }
    };

    determineApiUrl();
  }, []);

  // Generer cache-nøkkel inkludert minutter
  const getCacheKey = useCallback((date, time) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const [hour, minute] = time.split(':').map(Number);
    return `${year}-${month}-${day}-${hour}-${minute}`;
  }, []);

  // Hent siste bilde ved oppstart
  const fetchLatestImage = useCallback(async () => {
    if (!apiBaseUrl) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/api/latest`);

      if (!response.ok) {
        throw new Error(`Feil: ${response.status}`);
      }

      const imageBlob = await response.blob();
      const imageUrl = URL.createObjectURL(imageBlob);
      blobUrlsRef.current.add(imageUrl);

      // Hent metadata fra headers
      const year = parseInt(response.headers.get('X-Image-Year'));
      const month = parseInt(response.headers.get('X-Image-Month'));
      const day = parseInt(response.headers.get('X-Image-Day'));
      const hour = parseInt(response.headers.get('X-Image-Hour'));
      const minute = parseInt(response.headers.get('X-Image-Minute')) || 0;

      const date = new Date(year, month - 1, day);
      const time = `${hour}:${String(minute).padStart(2, '0')}`;

      setSelectedDate(date);
      setSelectedTime(time);
      setImage(imageUrl);
      setLoading(false);

      // Cache bildet
      const cacheKey = getCacheKey(date, time);
      setImageCache(prev => ({ ...prev, [cacheKey]: imageUrl }));
    } catch (err) {
      setError(`Kunne ikke laste siste bilde: ${err.message}`);
      setLoading(false);
      // Fallback til dagens dato med nærmeste gyldige tidspunkt
      const now = new Date();
      setSelectedDate(now);
      // Finn nærmeste gyldige tidspunkt
      const hour = now.getHours();
      const minute = Math.floor(now.getMinutes() / 20) * 20;
      const timeStr = `${hour}:${String(minute).padStart(2, '0')}`;
      // Sjekk om tidspunktet finnes i HOURS, ellers bruk nærmeste
      const fallbackTime = HOURS.includes(timeStr) ? timeStr : HOURS.find(t => parseInt(t) >= hour) || '12:00';
      setSelectedTime(fallbackTime);
      setIsLatestMode(false);
    }
  }, [apiBaseUrl, getCacheKey]);

  // Hent bilde for spesifikk dato/tid
  const fetchImage = useCallback(async (date, time, updateState = true) => {
    if (!apiBaseUrl || !date || !time) return null;

    const cacheKey = getCacheKey(date, time);

    // Returner fra cache hvis tilgjengelig
    if (imageCache[cacheKey]) {
      if (updateState) {
        setImage(imageCache[cacheKey]);
        setLoading(false);
      }
      return imageCache[cacheKey];
    }

    if (updateState) {
      setLoading(true);
      setError(null);
    }

    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const [hour, minute] = time.split(':').map(Number);

      const response = await fetch(
        `${apiBaseUrl}/api/images?year=${year}&month=${month}&day=${day}&hour=${hour}&minute=${minute}`,
        { cache: 'default' }
      );

      if (!response.ok) {
        throw new Error(`Feil: ${response.status} - ${response.statusText}`);
      }

      const imageBlob = await response.blob();
      const imageUrl = URL.createObjectURL(imageBlob);
      blobUrlsRef.current.add(imageUrl);

      // Oppdater cache
      setImageCache(prev => ({ ...prev, [cacheKey]: imageUrl }));

      if (updateState) {
        setImage(imageUrl);
        setLoading(false);
      }

      return imageUrl;
    } catch (err) {
      if (updateState) {
        setError(`Kunne ikke laste bildet: ${err.message}`);
        setImage(null);
        setLoading(false);
      }
      return null;
    }
  }, [apiBaseUrl, getCacheKey, imageCache]);

  // Preload nærliggende bilder (kun bakover i tid, ikke fremtiden)
  const preloadAdjacentImages = useCallback((date, time) => {
    if (!apiBaseUrl || !date || !time) return;

    const timeIndex = HOURS.indexOf(time);
    const now = new Date();

    // Preload neste bilde - men ikke hvis det er i fremtiden
    if (timeIndex < HOURS.length - 1) {
      const [nextHour, nextMin] = HOURS[timeIndex + 1].split(':').map(Number);
      const nextTime = new Date(date);
      nextTime.setHours(nextHour, nextMin, 0, 0);

      if (nextTime <= now) {
        fetchImage(date, HOURS[timeIndex + 1], false);
      }
    }

    // Preload forrige bilde
    if (timeIndex > 0) {
      fetchImage(date, HOURS[timeIndex - 1], false);
    }
  }, [fetchImage, apiBaseUrl]);

  // Hent siste bilde ved oppstart
  useEffect(() => {
    if (apiBaseUrl && isLatestMode && !selectedDate) {
      fetchLatestImage();
    }
  }, [apiBaseUrl, isLatestMode, selectedDate, fetchLatestImage]);

  // Hent bilde når dato/tid endres (ikke i latest mode)
  useEffect(() => {
    if (apiBaseUrl && selectedDate && selectedTime && !isLatestMode) {
      fetchImage(selectedDate, selectedTime);

      const preloadTimer = setTimeout(() => {
        preloadAdjacentImages(selectedDate, selectedTime);
      }, 500);

      return () => clearTimeout(preloadTimer);
    }
  }, [selectedDate, selectedTime, isLatestMode, fetchImage, preloadAdjacentImages, apiBaseUrl]);

  // Rydd opp object URLs ved unmount (ikke ved cache-endringer)
  useEffect(() => {
    const urlsRef = blobUrlsRef;
    return () => {
      urlsRef.current.forEach(url => {
        URL.revokeObjectURL(url);
      });
      urlsRef.current.clear();
    };
  }, []);

  // Escape-tast lukker fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Hent thumbnails for andre ar (2 tilbake, 1 tilbake, 1 frem, 2 frem)
  // VIKTIG: Venter til hovedbildet er HELT ferdig lastet
  useEffect(() => {
    if (!apiBaseUrl || !selectedDate || !selectedTime) return;

    // Vent til hovedbildet er ferdig lastet OG vi har et bilde
    if (loading || !image) return;

    const fetchYearThumbnails = async () => {
      const [hour, minute] = selectedTime.split(':').map(Number);
      const currentYear = new Date().getFullYear();

      const getDateWithYearOffset = (offset) => {
        const date = new Date(selectedDate);
        date.setFullYear(date.getFullYear() + offset);
        // Ikke hent bilder fra fremtiden
        if (date.getFullYear() > currentYear) return null;
        return date;
      };

      // Hent thumbnails SEKVENSIELT for å ikke overbelaste nettverket
      const fetchThumb = async (date) => {
        if (!date) return null;
        try {
          const year = date.getFullYear();
          const month = date.getMonth() + 1;
          const day = date.getDate();
          const response = await fetch(
            `${apiBaseUrl}/api/thumbnails?year=${year}&month=${month}&day=${day}&hour=${hour}&minute=${minute}`
          );
          if (response.ok) {
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            blobUrlsRef.current.add(url);
            return { url, date };
          }
        } catch (e) {
          // Ignorer feil
        }
        return null;
      };

      // Hent sekvensielt i stedet for parallelt for å ikke blokkere
      const minusTwo = await fetchThumb(getDateWithYearOffset(-2));
      const minusOne = await fetchThumb(getDateWithYearOffset(-1));
      const plusOne = await fetchThumb(getDateWithYearOffset(1));
      const plusTwo = await fetchThumb(getDateWithYearOffset(2));

      setYearThumbnails({ minusTwo, minusOne, plusOne, plusTwo });
    };

    // Forsink thumbnail-henting med 500ms for å garantere at hovedbilde er vist
    const timer = setTimeout(fetchYearThumbnails, 500);
    return () => clearTimeout(timer);
  }, [apiBaseUrl, selectedDate, selectedTime, loading, image]);

  const handleDateChange = (e) => {
    setSelectedDate(new Date(e.target.value));
    setIsLatestMode(false);
  };

  const handleTimeChange = (e) => {
    setSelectedTime(e.target.value);
    setIsLatestMode(false);
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const navigateDate = (days) => {
    if (!selectedDate) return;
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() + days);
    setSelectedDate(newDate);
    setIsLatestMode(false);
  };

  const navigateYear = (years) => {
    if (!selectedDate) return;
    const newDate = new Date(selectedDate);
    newDate.setFullYear(selectedDate.getFullYear() + years);
    setSelectedDate(newDate);
    setIsLatestMode(false);
  };

  const navigateTime = (direction) => {
    if (!selectedDate || !selectedTime) return;
    const timeIndex = HOURS.indexOf(selectedTime);
    if (timeIndex === -1) return;

    if (direction === 1 && timeIndex < HOURS.length - 1) {
      // Neste tidspunkt
      setSelectedTime(HOURS[timeIndex + 1]);
      setIsLatestMode(false);
    } else if (direction === 1 && timeIndex === HOURS.length - 1) {
      // Gå til neste dag, første tidspunkt
      const newDate = new Date(selectedDate);
      newDate.setDate(selectedDate.getDate() + 1);
      setSelectedDate(newDate);
      setSelectedTime(HOURS[0]);
      setIsLatestMode(false);
    } else if (direction === -1 && timeIndex > 0) {
      // Forrige tidspunkt
      setSelectedTime(HOURS[timeIndex - 1]);
      setIsLatestMode(false);
    } else if (direction === -1 && timeIndex === 0) {
      // Gå til forrige dag, siste tidspunkt
      const newDate = new Date(selectedDate);
      newDate.setDate(selectedDate.getDate() - 1);
      setSelectedDate(newDate);
      setSelectedTime(HOURS[HOURS.length - 1]);
      setIsLatestMode(false);
    }
  };

  const goToLatest = () => {
    setIsLatestMode(true);
    setSelectedDate(null);
    setSelectedTime(null);
    fetchLatestImage();
  };

  // Åpne fullskjerm med full oppløsning (HD)
  const openHdFullscreen = async () => {
    if (!apiBaseUrl || !selectedDate || !selectedTime) return;

    setHdLoading(true);
    setIsFullscreen(true);
    setIsHdMode(true);

    try {
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const url = `${apiBaseUrl}/api/images?year=${selectedDate.getFullYear()}&month=${selectedDate.getMonth() + 1}&day=${selectedDate.getDate()}&hour=${hours}&minute=${minutes}&full=true`;

      const response = await fetch(url);
      if (!response.ok) throw new Error('Kunne ikke laste HD-bilde');

      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      blobUrlsRef.current.add(imageUrl);
      setHdImage(imageUrl);
    } catch (err) {
      console.error('Feil ved lasting av HD-bilde:', err);
      // Fall tilbake til vanlig bilde i fullskjerm
      setIsHdMode(false);
    } finally {
      setHdLoading(false);
    }
  };

  // Lukk fullskjerm og rydd opp HD-bilde
  const closeFullscreen = () => {
    setIsFullscreen(false);
    setIsHdMode(false);
    if (hdImage) {
      URL.revokeObjectURL(hdImage);
      blobUrlsRef.current.delete(hdImage);
      setHdImage(null);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Vinglebu webcam</h1>
        {apiBaseUrl && <small className="api-source">API: {apiBaseUrl}</small>}
      </header>
      <main>
        <div className="controls">
          <div className="date-navigation">
            <button onClick={() => navigateYear(-1)} disabled={!selectedDate} title="1 ar tilbake">
              -1 ar
            </button>
            <button onClick={() => navigateDate(-1)} disabled={!selectedDate}>
              Forrige dag
            </button>
            <button onClick={() => navigateTime(-1)} disabled={!selectedDate} title="Forrige bilde">
              &larr;
            </button>
            <input
              type="date"
              value={formatDateForInput(selectedDate)}
              onChange={handleDateChange}
            />
            <button onClick={() => navigateTime(1)} disabled={!selectedDate} title="Neste bilde">
              &rarr;
            </button>
            <button onClick={() => navigateDate(1)} disabled={!selectedDate}>
              Neste dag
            </button>
            <button onClick={() => navigateYear(1)} disabled={!selectedDate} title="1 ar frem">
              +1 ar
            </button>
            <button onClick={goToLatest} className={isLatestMode ? 'active' : ''}>
              Siste bilde
            </button>
          </div>
          <div className="time-selection">
            <label htmlFor="time">Tidspunkt:</label>
            <select
              id="time"
              value={selectedTime || ''}
              onChange={handleTimeChange}
              disabled={!selectedDate}
            >
              {HOURS.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={`image-container ${isFullscreen ? 'fullscreen' : ''}`}>
          {!apiBaseUrl && <div className="loading">Bestemmer API URL...</div>}
          {loading && apiBaseUrl && <div className="loading">Laster bilde...</div>}
          {error && <div className="error">{error}</div>}
          {image && !loading && (
            <div>
              <div className="image-wrapper">
                <img
                  src={isHdMode && hdImage ? hdImage : image}
                  alt={selectedDate ? `Bilde fra ${selectedDate.toLocaleDateString()} kl. ${selectedTime}` : 'Siste bilde'}
                  onClick={() => isFullscreen ? closeFullscreen() : setIsFullscreen(true)}
                />
                {hdLoading && <div className="hd-loading">Laster HD...</div>}
                <button
                  className="fullscreen-btn"
                  onClick={() => isFullscreen ? closeFullscreen() : setIsFullscreen(true)}
                  title={isFullscreen ? 'Lukk fullskjerm' : 'Fullskjerm'}
                >
                  {isFullscreen ? '✕' : '⛶'}
                </button>
                {!isFullscreen && selectedDate && (
                  <button
                    className="fullscreen-btn hd-btn"
                    onClick={openHdFullscreen}
                    title="Fullskjerm med full oppløsning"
                  >
                    HD
                  </button>
                )}
              </div>
              {!isFullscreen && (
                <p className="image-caption">
                  {isLatestMode && 'Siste bilde: '}
                  {selectedDate && selectedDate.toLocaleDateString('no-NO', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  {selectedTime && ` kl. ${selectedTime}`}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Thumbnails for andre ar */}
        {!isFullscreen && (yearThumbnails.minusTwo || yearThumbnails.minusOne || yearThumbnails.plusOne || yearThumbnails.plusTwo) && (
          <div className="historic-thumbnails">
            <h3>Samme tidspunkt andre ar</h3>
            <div className="thumbnails-row">
              {yearThumbnails.minusTwo && (
                <div
                  className="thumbnail-item"
                  onClick={() => {
                    setSelectedDate(yearThumbnails.minusTwo.date);
                    setIsLatestMode(false);
                  }}
                >
                  <img
                    src={yearThumbnails.minusTwo.url}
                    alt={`2 ar siden`}
                  />
                  <p>{yearThumbnails.minusTwo.date.toLocaleDateString('no-NO', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}</p>
                </div>
              )}
              {yearThumbnails.minusOne && (
                <div
                  className="thumbnail-item"
                  onClick={() => {
                    setSelectedDate(yearThumbnails.minusOne.date);
                    setIsLatestMode(false);
                  }}
                >
                  <img
                    src={yearThumbnails.minusOne.url}
                    alt={`1 ar siden`}
                  />
                  <p>{yearThumbnails.minusOne.date.toLocaleDateString('no-NO', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}</p>
                </div>
              )}
              {yearThumbnails.plusOne && (
                <div
                  className="thumbnail-item"
                  onClick={() => {
                    setSelectedDate(yearThumbnails.plusOne.date);
                    setIsLatestMode(false);
                  }}
                >
                  <img
                    src={yearThumbnails.plusOne.url}
                    alt={`1 ar frem`}
                  />
                  <p>{yearThumbnails.plusOne.date.toLocaleDateString('no-NO', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}</p>
                </div>
              )}
              {yearThumbnails.plusTwo && (
                <div
                  className="thumbnail-item"
                  onClick={() => {
                    setSelectedDate(yearThumbnails.plusTwo.date);
                    setIsLatestMode(false);
                  }}
                >
                  <img
                    src={yearThumbnails.plusTwo.url}
                    alt={`2 ar frem`}
                  />
                  <p>{yearThumbnails.plusTwo.date.toLocaleDateString('no-NO', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
